import { action, computed, makeObservable, observable } from 'mobx';
import { Model } from './Model';
import { ModelArrayCast, ModelCast } from 'shared/casts';

export type Nullable<T> = T | null;
export type EntityId = string | number;
export type UnitId = 'currency' | 'percent' | 'percent_turnover';

export interface IUnitValue {
    amount: number;
    unit_id: UnitId;
    show_sign?: boolean;
    type?: 'main' | 'detailed';
}
export interface IUnitDefinition {
    long: string;
    short: string;
}

export const Unit = {
    currency: (amount = 0): IUnitValue => ({ amount, unit_id: 'currency' }),
    percent: (amount = 0): IUnitValue => ({ amount, unit_id: 'percent' }),
};

export interface IUserDashboardEmployee {
    id: EntityId;
    name: string;
    position_id: EntityId;
}

export interface IUserDashboardPosition {
    id: EntityId;
    name: string;
}

export interface IUserDashboardStore {
    id: EntityId;
    name: string;
}

export interface IUserDashboardPeriod {
    id: EntityId;
    name: string;
}

export interface IUserDashboardAssortment {
    id: EntityId;
    name: string;
    image: string;
}

export interface IUserDashboardEfficiencyItem {
    employee_id: EntityId;
    position_id: EntityId;
    period_id: EntityId;
    store_id: EntityId;
    plan: IUnitValue;
    fact: IUnitValue;
    value: IUnitValue;
}

export interface IUserDashboardLeagueGoal {
    name: string;
    value: {
        amount: number;
        unit_id: string;
    };
}

export class UserDashboardEfficiencyItemModel extends Model implements IUserDashboardEfficiencyItem {
    employee_id: EntityId = '';
    position_id: EntityId = '';
    period_id: EntityId = '';
    store_id: EntityId = '';
    plan: IUnitValue = Unit.currency();
    fact: IUnitValue = Unit.currency();
    value: IUnitValue = Unit.percent();

    constructor(payload: Partial<IUserDashboardEfficiencyItem> = {}) {
        super();
        makeObservable(this, {
            employee_id: observable,
            position_id: observable,
            period_id: observable,
            store_id: observable,
            plan: observable,
            fact: observable,
            value: observable,
            update: action
        })
        this.update(payload);
    }
}

export interface IUserDashboardEfficiency {
    items?: IUserDashboardEfficiencyItem[];
}

export class UserDashboardEfficiency extends Model implements IUserDashboardEfficiency {
    items: UserDashboardEfficiencyItemModel[] = [];
    casts = { items: new ModelArrayCast(UserDashboardEfficiencyItemModel) };

    constructor(payload: Partial<IUserDashboardEfficiency> = {}) {
        super();
        makeObservable(this, {
            items: observable,
            update: action
        })
        this.update(payload);
    }
}


type UnitValueInput = Nullable<IUnitValue | IUnitValue[]> ;

export interface IStatisticItem {
    amount: number;
    unit_id: UnitId;
    month?: number;
    year: number;
}

export interface IUserDashboardWidgetItemModel {
    store_id?: Nullable<EntityId>;
    group_id?: Nullable<EntityId>;
    plan?: UnitValueInput;
    plan_hint?: string | { main?: string; detailed?: string; };
    fact?: UnitValueInput;
    fact_hint?: string | { main?: string; detailed?: string; };
    value?: IUnitValue;
    statistic?: IStatisticItem[];
    date?: string;
    is_workday?: boolean;
    is_current?: boolean;
}

export class UserDashboardWidgetItemModel extends Model implements IUserDashboardWidgetItemModel {
    store_id: Nullable<EntityId> = null;
    group_id: Nullable<EntityId> = null;
    plan: UnitValueInput = null;
    plan_hint: string | { main?: string; detailed?: string; } = '';
    fact: UnitValueInput = null;
    fact_hint: string | { main?: string; detailed?: string; } = '';
    value: IUnitValue = Unit.percent();
    date: string = '';
    is_workday: boolean = false;
    is_current: boolean = false;

    constructor(payload: Partial<IUserDashboardWidgetItemModel> = {}) {
        super();
        makeObservable(this, {
            store_id: observable,
            group_id: observable,
            plan: observable,
            plan_hint: observable,
            fact: observable,
            fact_hint: observable,
            value: observable,
            date: observable,
            is_workday: observable,
            is_current: observable,
            statistic: observable,
            update: action
        })
        this.update(payload)
    }
}

export interface IUserDashboardBonus {
    name: string;
    value: IUnitValue;
    status: 'locked' | 'completed' | 'previous';
    note?: string;
    sign?: string;
    group_id?: string;
}

export interface IUserDashboardBonusItem {
    title?: string;
    group_id: string;
    items: IUserDashboardBonus[];
}

export interface IUserDashboardPercentGridItem {
    plan_threshold?: string;
    percent?: IUnitValue;
    note?: string;
}
export interface IUserDashboardBonusGroup {
    id: string;
    name: string;
    percent_grid?: {
        items?: IUserDashboardPercentGridItem[];
        description?: string;
    };
    total?: IUserDashboardBonusTotal;
}

export interface IUserDashboardBonusTotalExtraItem {
    name: string;
    value: IUnitValue;
}

export interface IUserDashboardBonusTotal {
    name?: string;
    value?: IUnitValue;
    extra?: IUserDashboardBonusTotalExtraItem[];
}

export interface IUserDashboardBonuses {
    status_id: "received" | "in_progress";
    groups: IUserDashboardBonusGroup[];
    items: IUserDashboardBonusItem[];
    total: IUserDashboardBonusTotal;
}

export interface IUserDashboardTotalBonuses {
    groups: IUserDashboardBonusGroup[];
    items: IUserDashboardBonus[];
    total: IUserDashboardBonusTotal;
}

export class UserDashboardBonusesModel extends Model implements IUserDashboardBonuses {
    status_id: "received" | "in_progress" = "in_progress";
    groups: IUserDashboardBonusGroup[] = [];
    items: IUserDashboardBonusItem[] = [];
    total: IUserDashboardBonusTotal = {};

    constructor(payload: Partial<IUserDashboardBonuses> = {}) {
        super();
        makeObservable(this, {
            status_id: observable,
            groups: observable,
            items: observable,
            total: observable,
            update: action,
        })
        this.update(payload);
    }
}

export type IUserDashboardLabels = Record<string, string>;

export interface IUserDashboardWidgetModel {
    type_id: string;
    name: string;
    labels?: Record<string, string>;
    items_group_type?: Nullable<EntityId>;
    items: IUserDashboardWidgetItemModel[];
    bonuses?: IUserDashboardBonuses;
    stores_view?: 'list' | 'modal';
    stores_modal_title?: string;
    efficiency?: IUserDashboardEfficiency;
}

export class UserDashboardWidgetModel extends Model implements IUserDashboardWidgetModel {
    type_id = '';
    name = '';
    group_id: Nullable<EntityId> = null;
    labels: IUserDashboardLabels = {};
    items_group_type?: EntityId;
    items: UserDashboardWidgetItemModel[] = [];
    bonuses?: UserDashboardBonusesModel = new UserDashboardBonusesModel();
    stores_view?: 'list' | 'modal' = 'list';
    stores_modal_title?: string;
    efficiency?: UserDashboardEfficiency = new UserDashboardEfficiency();

    casts = {
        items: new ModelArrayCast(UserDashboardWidgetItemModel),
        bonuses: new ModelCast(UserDashboardBonusesModel),
        efficiency: new ModelCast(UserDashboardEfficiency)
    };

    constructor(payload: Partial<IUserDashboardWidgetModel> = {}) {
        super();
        makeObservable(this, {
            type_id: observable,
            name: observable,
            group_id: observable,
            labels: observable,
            items_group_type: observable,
            items: observable,
            bonuses: observable,
            stores_view: observable,
            stores_modal_title: observable,
            efficiency: observable,
            update: action
        })
        this.update(payload);
    }
}

export type IUserDashboardBonusesModel = IUserDashboardBonuses

export interface IUserDashboardContentItemModel {
    bonuses?: IUserDashboardBonusesModel;
    groups?: IUserDashboardGroup[];
    widgets?: IUserDashboardWidgetModel[];
}
export class UserDashboardGroupModel extends Model implements IUserDashboardGroup {
    id: string = '';
    name: string = '';

    constructor(payload: Partial<IUserDashboardGroup> = {}) {
        super();
        makeObservable(this, {
            id: observable,
            name: observable,
            update: action,
        })
        this.update(payload);
    }
}

export class UserDashboardContentItemModel extends Model implements IUserDashboardContentItemModel {
    bonuses?: UserDashboardBonusesModel;
    groups: IUserDashboardGroup[] = [];
    widgets: UserDashboardWidgetModel[] = [];

    casts = {
        widgets: new ModelArrayCast(UserDashboardWidgetModel),
        bonuses: new ModelCast(UserDashboardBonusesModel),
        groups: new ModelArrayCast(UserDashboardGroupModel)
    };

    constructor(payload: Partial<IUserDashboardModel> = {}) {
        super();
        makeObservable(this, {
            email: observable,
            content: observable,
            update: action,
        });
        this.update(payload);
    }
}

export interface IUserDashboardGroup {
    id: string;
    name: string;
}

export type UserDashboardGroupType = "product" | "assortment" | "positions";

export type IUserDashboardGroups = Partial<Record<UserDashboardGroupType, IUserDashboardGroup[]>>;

export type DashboardPeriod = 'month' | 'quarter' | 'year';

export type IUserDashboardUnits = Partial<Record<UnitId, IUnitDefinition>>;

export interface IUserDashboardModel {
    email?: string;
    updated_at?: string;
    units?: IUserDashboardUnits;
    stores?: IUserDashboardStore[];
    employees?: IUserDashboardEmployee[];
    positions?: IUserDashboardPosition[];
    periods?: IUserDashboardPeriod[];
    assortment?: IUserDashboardAssortment[];
    groups?: IUserDashboardGroups;
    content?: {
        month?: UserDashboardContentItemModel | null;
        quarter?: UserDashboardContentItemModel | null;
        year?: UserDashboardContentItemModel | null;
    };
    bonuses?: IUserDashboardTotalBonuses;
}

export class UserDashboardModel extends Model implements IUserDashboardModel {
    email = '';
    updated_at = '';
    units: IUserDashboardUnits = {};
    stores: IUserDashboardStore[] = [];
    employees: IUserDashboardEmployee[] = [];
    positions: IUserDashboardPosition[] = [];
    periods: IUserDashboardPeriod[] = [];
    assortment: IUserDashboardAssortment[] = []
    groups: IUserDashboardGroups = {};

    content: {
        month?: UserDashboardContentItemModel | null;
        quarter?: UserDashboardContentItemModel | null;
        year?: UserDashboardContentItemModel | null;
    } = {};

    bonuses: IUserDashboardTotalBonuses;

    casts = {
        'content.month': new ModelCast(UserDashboardContentItemModel),
        'content.quarter': new ModelCast(UserDashboardContentItemModel),
        'content.year': new ModelCast(UserDashboardContentItemModel),
    };

    constructor(payload: Partial<IUserDashboardModel> = {}) {
        super();
        makeObservable(this, {
            email: observable,
            updated_at: observable,
            units: observable,
            stores: observable,
            employees: observable,
            positions: observable,
            periods: observable,
            assortment: observable,
            groups: observable,
            content: observable,
            bonuses: observable,
            update: action,
        });
        this.update(payload);
    }

    get tabs() {
        const tabs: { id: DashboardPeriod; name: string }[] = [];
        if (this.content?.month) tabs.push({ id: 'month', name: 'Месяц' });
        if (this.content?.quarter) tabs.push({ id: 'quarter', name: 'Квартал' });
        if (this.content?.year) tabs.push({ id: 'year', name: 'Год' });
        return tabs;
    }
}

export interface IUserDashboardResponseModel {
    item:  {
        content: IUserDashboardModel
    } | null
}

export class UserDashboardResponseModel
    extends Model
    implements IUserDashboardResponseModel
{
    item: {
        content: UserDashboardModel;
    } | null = {
            content: new UserDashboardModel(),
        };

    casts = {
        'item.content': new ModelCast(UserDashboardModel),
    };

    constructor(payload: Partial<IUserDashboardResponseModel> = {}) {
        super();

        makeObservable(this, {
            item: observable,
            update: action,
        });

        this.update(payload);
    }
}
